import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOrganizations, deleteOrganization } from '../../../services/organizations';
import DataTable from '../../../components/Tables/DataTable';
import Modal from '../../../components/Common/Modal';
import Toast from '../../../components/Common/Toast';
import { Plus, Eye, Edit2, Trash2, Building2, Search, AlertCircle } from 'lucide-react';

const OrganizationList = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getOrganizations();
      // Handle array or object-wrapped responses
      const data = Array.isArray(res) ? res : res?.data || [];
      setOrganizations(data);
    } catch (err) {
      setToast({ type: 'error', message: 'Connection Error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleDelete = async (id) => {
    try {
      await deleteOrganization(id);
      setToast({ type: 'success', message: 'Organization removed' });
      loadData();
    } catch (err) {
      setToast({ type: 'error', message: 'Delete failed' });
    } finally { setDeleteModal(null); }
  };

  const filteredOrgs = organizations.filter(org => 
    org?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org?.org_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { 
      key: 'name', 
      label: 'Organization',
      render: (row) => {
        // If row is a string (because DataTable only passed row['name']), 
        // we use that string. Otherwise we use row.name.
        const name = typeof row === 'string' ? row : row?.name;
        const code = row?.org_code || 'N/A';

        return (
          <div className="flex items-center gap-3 py-1 text-left">
            <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 border border-slate-200">
              <Building2 size={20} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-slate-900 truncate">
                {name || 'Unnamed Org'}
              </span>
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">
                {code}
              </span>
            </div>
          </div>
        );
      }
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (row) => {
        const status = (typeof row === 'string' ? row : row?.status) || 'unknown';
        const isActive = status.toLowerCase() === 'active';
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
            isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'
          }`}>
            {status}
          </span>
        );
      }
    },
    { 
      key: 'createdAt', 
      label: 'Date Created',
      render: (row) => {
        const dateVal = typeof row === 'string' ? row : row?.createdAt;
        if (!dateVal) return <span className="text-slate-300 italic text-xs">---</span>;
        return (
          <span className="text-slate-600 text-sm font-medium">
            {new Date(dateVal).toLocaleDateString('en-GB')}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Link to={`/dashboard/super-admin/organizations/${row?.id}`} className="p-2 text-slate-400 hover:text-indigo-600"><Eye size={18} /></Link>
          <Link to={`/dashboard/super-admin/organizations/${row?.id}/edit`} className="p-2 text-slate-400 hover:text-amber-600"><Edit2 size={18} /></Link>
          <button onClick={() => setDeleteModal(row)} className="p-2 text-slate-400 hover:text-rose-600"><Trash2 size={18} /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-8 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Organizations</h1>
          <p className="text-slate-500 font-medium italic">Manage system-wide welfare associations</p>
        </div>
        <Link to="/dashboard/super-admin/organizations/new">
          <button className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-600 transition-all shadow-lg active:scale-95">
            + Create New Association
          </button>
        </Link>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input 
          type="text" 
          placeholder="Search by name or code..."
          className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500/20 transition-all shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden">
        {loading ? (
          <div className="py-24 text-center text-slate-400 animate-pulse font-bold uppercase tracking-widest text-xs">Loading Registry...</div>
        ) : filteredOrgs.length === 0 ? (
          <div className="py-24 text-center text-slate-400">
            <AlertCircle className="mx-auto mb-4" size={48} strokeWidth={1} />
            <p className="font-bold text-slate-900">No Associations Found</p>
            <p className="text-sm italic">Try a different search term</p>
          </div>
        ) : (
          <DataTable columns={columns} data={filteredOrgs} />
        )}
      </div>

      {deleteModal && (
        <Modal isOpen={true} onClose={() => setDeleteModal(null)} title="Confirm Action">
          <div className="p-4 text-center space-y-6">
            <div className="h-16 w-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto ring-4 ring-rose-50">
              <Trash2 size={32} />
            </div>
            <p className="text-slate-600">Delete <span className="font-black text-slate-900 uppercase">"{deleteModal?.name}"</span>? This cannot be undone.</p>
            <div className="flex flex-col gap-2">
              <button onClick={() => handleDelete(deleteModal.id)} className="w-full py-3 bg-rose-600 text-white font-bold rounded-xl shadow-lg shadow-rose-100">Confirm Deletion</button>
              <button onClick={() => setDeleteModal(null)} className="w-full py-3 bg-slate-100 text-slate-500 font-bold rounded-xl">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default OrganizationList;